import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
// 3rd party libs
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import { useOnClickOutside } from 'usehooks-ts';

// local imports
import { useAppSelector } from '../../../store/hooks';
import { GROUP_WITH_SUBGROUP, USER } from '../../../enums/TYPES';
import UsersIcon from '../../../assets/icons/users.svg';
import TreeIcon from '../../../assets/icons/tree.svg';
import { DateFormatter } from '../../../helpers';
import { updateGroupUsers } from '../../../api';
import LinearProgress from '../LinearProgress';
import { NOTIFICATION } from '../../../enums';
import { PATHS } from '../../../enums/PATHS';
import { ROLES } from '../../../enums/ROLES';
import styles from './GroupCard.module.scss';
import TrashButton from '../TrashButton';
import EditButton from '../EditButton';
import { MoreIcon } from '../../../assets/icons/threedots';
import ApplyPreferencesModal from '../ApplyPreferencesModal';

type TGroupCard = {
  data: GROUP_WITH_SUBGROUP;
  onEdit: (group: GROUP_WITH_SUBGROUP) => void;
  toDelete?: (id: string) => void;
  setFetchTrigger: (num: number) => void;
};

const GroupCard: React.FC<TGroupCard> = ({ data, onEdit, toDelete, setFetchTrigger }) => {
  const canViewEmails = useCanViewEmails();
  const history = useHistory();
  const [allUsers, setAllUsers] = useState<Omit<USER, 'isActive' | 'updated_at' | 'forgot_password_token'>[]>([]);

  useEffect(() => {
    const users = [...data?.analysts, ...data?.viewers].filter(
      (value, index, self) => index === self.findIndex((user) => user.id === value.id),
    );
    setAllUsers(users);
  }, [data?.analysts, data?.viewers]);

  const userRole = useAppSelector((state) => state.user.role);
  const [hover, setHover] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(data.title);
  const ref = useRef<HTMLDivElement | null>(null);

  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  const notification = useSnackbar();

  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (isEditing && ref.current && !ref.current.contains(e.target as Node)) {
        setIsEditing(false);
        setHover(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isEditing]);

  const onDelete = () => {
    if (toDelete) {
      toDelete(data.id);
    }
  };

  const handleEditClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    onEdit(data);
  };

  const [loading, setLoading] = useState(false);
  const handleTitleSubmit = async (e: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    e.preventDefault();

    if (title === data.title) {
      setHover(false);
      setIsEditing(false);
      return;
    } else if (title.trim().length === 0) {
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      setTitle(data.title);
      setHover(false);
      setIsEditing(false);
      return;
    }

    setIsEditing(false);

    try {
      setLoading(true);
      await updateGroupUsers({
        title,
        groupdId: data.id,
      });

      setFetchTrigger(Date.now());
      notification.showMessage(NOTIFICATION.UPDATED);

      setHover(false);
    } catch (error) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }

      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleTitleEdit = () => {
    if (userRole === ROLES.ORGANIZATION_ADMIN) {
      setIsEditing(!isEditing);
    } else {
      history.push(`${PATHS.SUB_GROUPS_MANAGEMENT.path}${data.id}`, data.id);
    }
  };

  const onRedirect = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.target as Node) && [ROLES.ORGANIZATION_ADMIN, ROLES.ANALYST, ROLES.VIEWER].includes(userRole!)) {
      history.push(`${PATHS.SUB_GROUPS_MANAGEMENT.path}${data.id}`, data.id);
    }
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (showDotsMenu) {
      setShowDotsMenu(false);
      return;
    }

    const cardElement = (e.currentTarget.closest(`.${styles.card}`) as HTMLElement) || ref.current;
    if (!cardElement) return;

    const cardRect = cardElement.getBoundingClientRect();
    const menuWidth = actionMenuRef.current?.offsetWidth || 180;
    const menuHeight = actionMenuRef.current?.offsetHeight || 200;

    let posX = e.clientX - cardRect.left;
    let posY = e.clientY - cardRect.top;

    // horizontal clamp: keep inside card
    if (posX + menuWidth > cardRect.width) {
      posX = cardRect.width - menuWidth - 10;
    }
    if (posX < 0) {
      posX = 0;
    }

    const p = cardRect.bottom + menuHeight - window.innerHeight;
    // vertical clamp vs viewport bottom (ComposerFolder-style)
    if (cardRect.bottom + menuHeight > window.innerHeight) {
      if (p > 90) {
        posY -= menuHeight - 100;
      }
    }

    setMenuPosition({
      x: posX,
      y: posY,
    });

    setShowDotsMenu(true);
  };

  return (
    <div className={styles.card} ref={ref} onClick={onRedirect} onContextMenu={switchDotsMenu} style={{ position: 'relative' }}>
      {isEditing ? (
        <div className={styles.inputContainer}>
          <form
            onClick={(e) => {
              e.stopPropagation();
            }}
            onSubmit={handleTitleSubmit}
          >
            <input className={styles.input} onChange={handleTitleChange} defaultValue={data.title} onBlur={handleTitleEdit} />
          </form>
        </div>
      ) : (
        <div className={styles.title} onClick={onRedirect}>
          <span onClick={onRedirect}>{data.title}</span>
          {loading && <LinearProgress />}
        </div>
      )}

      <div className={styles.details} onClick={onRedirect}>
        <Tooltip
          placement="right"
          arrow
          title={
            <div onClick={onRedirect} className={styles.tooltip}>
              {allUsers.map((u) => (
                <div key={`${u.id}_tooltip`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ marginRight: '8px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')} </span>{' '}
                  <span>{u?.role?.title.replace('Organization', 'Organisation')}</span>
                </div>
              ))}
            </div>
          }
        >
          <div className={styles.data} onClick={onRedirect}>
            <img src={UsersIcon} alt="users" />
            {allUsers.length}
          </div>
        </Tooltip>
        {data?.subgroups.length > 0 && (
          <Tooltip
            placement="right"
            arrow
            title={
              <div onClick={onRedirect}>
                <div style={{ marginBottom: '6px' }}>Subgroups:</div>
                {data.subgroups.map((s) => (
                  <div key={`${s.id}_tooltip`}> {s.title} </div>
                ))}
              </div>
            }
          >
            <div className={styles.data} onClick={onRedirect}>
              <img src={TreeIcon} alt="subgroups" /> {data.subgroups.length}
            </div>
          </Tooltip>
        )}
      </div>
      <div className={styles.created} onClick={onRedirect}>
        {DateFormatter(data.created_at, true)}
      </div>
      <div className={styles.updated} onClick={onRedirect}>
        {data?.updated_at && DateFormatter(data?.updated_at, true)}
      </div>

      {userRole === ROLES.ORGANIZATION_ADMIN && (
        <div className={styles.action}>
          <div className={styles.dotsMenuBtn} onClick={switchDotsMenu}>
            <div className={styles.dotIcon}>
              <MoreIcon />
            </div>
          </div>
        </div>
      )}

      {userRole === ROLES.ORGANIZATION_ADMIN && (
        <div
          ref={actionMenuRef}
          className={styles.actionMenu}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            display: showDotsMenu ? 'block' : 'none',
            top: `${menuPosition.y}px`,
            left: `${menuPosition.x}px`,
          }}
        >
          <div className={styles.menuOption} onClick={handleEditClick}>
            <EditButton /> Edit
          </div>
          <div
            className={styles.menuOption}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowDotsMenu(false);
              setShowPreferencesModal(true);
            }}
          >
            <EditButton /> Preferences
          </div>
          <div
            className={styles.menuOption}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowDotsMenu(false);
              onDelete();
            }}
          >
            <TrashButton /> Delete
          </div>
        </div>
      )}
      <ApplyPreferencesModal
        open={showPreferencesModal}
        onClose={() => setShowPreferencesModal(false)}
        title={`${data.title} Preferences`}
        fixedScope="group"
        fixedGroupId={Number(data.id)}
      />
    </div>
  );
};

export default GroupCard;
